import { hasCurrentView } from "../../shared/utils/view-util";
import { bindRandsMask } from "../../shared/utils/currency-util";
import { initAddressAutocomplete } from "../../shared/utils/address-autofill-utils";
import {
  initFlatpickrOnSelector,
  isLegacyFlatpickrReady,
  parseFlatpickrDate,
} from "../../shared/flatpickr";
import moment from "moment";
import Rails from "@rails/ujs";

document.addEventListener("DOMContentLoaded", () => {
  if (
    !hasCurrentView([
      ".portal.events.new",
      ".portal.events.create",
      ".portal.events.edit",
      ".portal.events.update",
    ])
  ) {
    return;
  }

  const setupEditBackNavigation = () => {
    if (!hasCurrentView([".portal.events.edit", ".portal.events.update"]))
      return;

    const editPage = document.getElementById("event-edit-page");
    const eventsHomePath =
      editPage && editPage.dataset ? editPage.dataset.eventsHomePath : null;
    if (!eventsHomePath || !window.history || !window.history.pushState) return;

    // Keep edit-page browser back behavior consistent with in-page cancel/back links.
    window.history.pushState(
      { eventEditBackGuard: true },
      "",
      window.location.href,
    );
    window.addEventListener("popstate", () => {
      window.location.assign(eventsHomePath);
    });
  };

  setupEditBackNavigation();

  const escapeHtml = (str) => {
    if (str == null) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  };

  const container = document.getElementById("ticket-options-container");
  if (!container) return;

  let ticketOptionIndex =
    parseInt(container.getAttribute("data-next-index"), 10) || 1;

  const MAX_EVENT_CATEGORIES = 3;
  const _categoryState = { selected: [] };

  const TICKET_SALES_DURATION = {
    THROUGHOUT: "throughout",
    BEFORE_END: "before_end",
  };

  const TICKET_CAPACITY = {
    UNLIMITED: "unlimited",
    LIMITED: "limited",
  };

  const isLimitedTicketCapacitySelected = () => {
    const radio = document.querySelector(
      'input[name="event_form[ticket_capacity]"]:checked',
    );
    return radio && radio.value === TICKET_CAPACITY.LIMITED;
  };

  const isCloseBeforeEndSelected = () => {
    const radio = document.querySelector(
      'input[name="event_form[ticket_sales_duration]"]:checked',
    );
    return radio && radio.value === TICKET_SALES_DURATION.BEFORE_END;
  };

  const closeOnChange = (_selectedDates, _dateStr, instance) => {
    if (!instance || instance.__forceCloseOnChange) return;
    instance.__forceCloseOnChange = true;
    try {
      instance.close();
      if (instance._input) instance._input.blur();
    } finally {
      instance.__forceCloseOnChange = false;
    }
  };

  const closeOnDayClick = (_selectedDates, _dateStr, instance) => {
    if (
      !instance ||
      !instance.calendarContainer ||
      instance.__dayClickCloseBound
    )
      return;
    instance.__dayClickCloseBound = true;
    instance.calendarContainer.addEventListener("click", (event) => {
      const dayEl = event.target.closest(".flatpickr-day");
      if (!dayEl || dayEl.classList.contains("flatpickr-disabled")) return;
      requestAnimationFrame(() => instance.close());
    });
  };

  const flatpickrDateOptions = (onChange) => ({
    dateFormat: "Y-m-d",
    allowInput: false,
    static: true,
    closeOnSelect: true,
    onChange,
    onReady: closeOnDayClick,
  });

  const flatpickrTimeOptions = {
    enableTime: true,
    noCalendar: true,
    dateFormat: "H:i",
    time_24hr: true,
    allowInput: false,
    static: true,
    closeOnSelect: true,
    onChange: closeOnChange,
  };

  let salesCloseDatePicker = null;

  const parseFormDate = (dateStr) => parseFlatpickrDate(dateStr, "Y-m-d");

  const syncSalesCloseDateBounds = () => {
    if (
      !salesCloseDatePicker ||
      typeof salesCloseDatePicker.set !== "function"
    ) {
      return;
    }

    salesCloseDatePicker.set("minDate", "");
    salesCloseDatePicker.set("maxDate", "");

    if (!isCloseBeforeEndSelected()) return;

    const endDateEl = document.getElementById("event_end_date");
    const maxDate = parseFormDate(endDateEl && endDateEl.value);
    if (!maxDate) return;

    const minTime = moment().startOf("day").valueOf();
    salesCloseDatePicker.set("minDate", new Date(minTime));
    salesCloseDatePicker.set("maxDate", maxDate);

    const salesCloseDateEl = document.getElementById(
      "event_ticket_sales_close_date",
    );
    const selectedCloseDate = parseFormDate(
      salesCloseDateEl && salesCloseDateEl.value,
    );
    if (!selectedCloseDate) return;

    const selectedTime = selectedCloseDate.getTime();
    if (selectedTime < minTime || selectedTime > maxDate.getTime()) {
      salesCloseDatePicker.clear();
    }
  };

  const onEndDateChange = (selectedDates, dateStr, instance) => {
    closeOnChange(selectedDates, dateStr, instance);
    syncSalesCloseDateBounds();
  };

  const initEventFlatpickr = () => {
    if (!isLegacyFlatpickrReady()) return false;

    try {
      initFlatpickrOnSelector(
        "#event_start_date",
        flatpickrDateOptions(closeOnChange),
      );
      initFlatpickrOnSelector(
        "#event_end_date",
        flatpickrDateOptions(onEndDateChange),
      );
      initFlatpickrOnSelector("#event_start_time", flatpickrTimeOptions);
      initFlatpickrOnSelector("#event_end_time", flatpickrTimeOptions);
      salesCloseDatePicker = initFlatpickrOnSelector(
        "#event_ticket_sales_close_date",
        Object.assign({}, flatpickrDateOptions(closeOnChange), {
          onOpen: syncSalesCloseDateBounds,
        }),
      );
      initFlatpickrOnSelector(
        "#event_ticket_sales_close_time",
        flatpickrTimeOptions,
      );
    } catch (error) {
      console.error("Failed to initialize event date/time pickers", error);
      return false;
    }

    return true;
  };

  if (!initEventFlatpickr()) {
    window.addEventListener("load", initEventFlatpickr, { once: true });
  }

  const ticketTypeModal = document.getElementById("ticket-type-select-modal");
  let activeTicketTypeRow = null;

  const markSelectedTicketTypeOption = (selectedRef) => {
    if (!ticketTypeModal) return;
    ticketTypeModal
      .querySelectorAll(".ticket-type-option")
      .forEach((option) => {
        const isSelected =
          !!selectedRef && option.dataset.externalReference === selectedRef;
        option.classList.toggle("is-selected", isSelected);
        option.setAttribute("aria-current", isSelected ? "true" : "false");
        const check = option.querySelector(".ticket-type-option-check");
        check?.classList.toggle("tw-opacity-0", !isSelected);
        check?.classList.toggle("tw-opacity-100", isSelected);
      });
  };

  const clearTicketTypeError = (row) => {
    const group = row
      .querySelector(".ticket-type-trigger")
      ?.closest(".form-group");
    if (!group) return;
    group.classList.remove("is-invalid");
    group.querySelector(".form-field-error")?.remove();
  };

  // Bootstrap opens/closes the modal via its data-api; this only tracks which
  // row the modal was opened from and applies the choice back to that row.
  const trackActiveTicketTypeRow = (trigger) => {
    activeTicketTypeRow = trigger.closest(".ticket-option-row");
    const selectedRef = activeTicketTypeRow?.querySelector(
      ".ticket-type-ref-hidden",
    )?.value;
    markSelectedTicketTypeOption(selectedRef);
  };

  const applyTicketTypeSelection = (externalReference, name) => {
    const row = activeTicketTypeRow;
    if (!row) return;

    const refInput = row.querySelector(".ticket-type-ref-hidden");
    const nameInput = row.querySelector(".ticket-type-name-hidden");
    const trigger = row.querySelector(".ticket-type-trigger");
    const label = trigger?.querySelector(".ticket-type-trigger-label");

    if (refInput) refInput.value = externalReference;
    if (nameInput) nameInput.value = name;
    if (label) label.textContent = name;
    if (trigger) trigger.classList.remove("select-placeholder", "is-invalid");
    clearTicketTypeError(row);
    markSelectedTicketTypeOption(externalReference);
  };

  const MIN_TICKET_TYPE_NAME_LENGTH = 2;

  const customTicketTypeSection = document.getElementById(
    "custom-ticket-types-section",
  );
  const customTicketTypeList = document.getElementById(
    "custom-ticket-type-list",
  );
  const createToggle = document.getElementById(
    "create-custom-ticket-type-toggle",
  );
  const createForm = document.getElementById("create-custom-ticket-type-form");
  const createNameInput = document.getElementById("custom-ticket-type-name");
  const createErrorEl = document.getElementById("custom-ticket-type-error");
  const createSubmitBtn = document.getElementById("custom-ticket-type-submit");
  const modalErrorEl = document.getElementById("ticket-type-modal-error");

  const showModalError = (message) => {
    if (!modalErrorEl) return;
    modalErrorEl.textContent = message;
    modalErrorEl.classList.remove("tw-hidden");
  };

  const clearModalError = () => {
    if (!modalErrorEl) return;
    modalErrorEl.textContent = "";
    modalErrorEl.classList.add("tw-hidden");
  };

  const createFieldGroup = () =>
    createNameInput?.closest(".input-float-label");

  const showCreateError = (message) => {
    if (!createErrorEl) return;
    createErrorEl.textContent = message;
    createErrorEl.classList.remove("tw-hidden");
    createNameInput?.classList.add("is-invalid");
    createFieldGroup()?.classList.add("is-invalid");
  };

  const clearCreateError = () => {
    if (!createErrorEl) return;
    createErrorEl.textContent = "";
    createErrorEl.classList.add("tw-hidden");
    createNameInput?.classList.remove("is-invalid");
    createFieldGroup()?.classList.remove("is-invalid");
  };

  const toggleCreateForm = (expanded) => {
    if (!createForm || !createToggle) return;
    createForm.classList.toggle("tw-hidden", !expanded);
    createToggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    if (expanded) createNameInput?.focus();
  };

  const resetCreateForm = () => {
    if (createNameInput) createNameInput.value = "";
    clearCreateError();
    toggleCreateForm(false);
  };

  const addCustomTicketTypeOption = (ticketType) => {
    const template = document.getElementById(
      "custom_ticket_type_option_template",
    );
    if (!template || !customTicketTypeList) return;

    customTicketTypeList.insertAdjacentHTML(
      "beforeend",
      template.innerHTML
        .replace(/__REF__/g, escapeHtml(ticketType.external_reference))
        .replace(/__NAME__/g, escapeHtml(ticketType.name)),
    );
    customTicketTypeSection?.classList.remove("tw-hidden");
  };

  const clearRowsUsingTicketType = (externalReference) => {
    container
      .querySelectorAll(".ticket-type-ref-hidden")
      .forEach((refInput) => {
        if (refInput.value !== externalReference) return;

        const row = refInput.closest(".ticket-option-row");
        // Saved options keep their ticket type even after it is archived.
        if (row.dataset.locked === "true") return;
        const nameInput = row.querySelector(".ticket-type-name-hidden");
        const trigger = row.querySelector(".ticket-type-trigger");
        const label = trigger?.querySelector(".ticket-type-trigger-label");

        refInput.value = "";
        if (nameInput) nameInput.value = "";
        if (label) label.textContent = "Select";
        trigger?.classList.add("select-placeholder");
      });
  };

  const ticketTypeRequest = (url, method, body) =>
    fetch(url, {
      method,
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token":
          document.querySelector('meta[name="csrf-token"]')?.content || "",
      },
      body: body ? JSON.stringify(body) : undefined,
    });

  const errorMessageFrom = async (response, fallback) => {
    try {
      const data = await response.json();
      return data.error || fallback;
    } catch {
      return fallback;
    }
  };

  const createCustomTicketType = async () => {
    if (!createNameInput || !createSubmitBtn) return;

    const name = createNameInput.value.trim();
    if (name.length < MIN_TICKET_TYPE_NAME_LENGTH) {
      showCreateError("Enter a ticket type name.");
      createNameInput.focus();
      return;
    }

    clearCreateError();
    Rails.disableElement(createSubmitBtn);

    try {
      const response = await ticketTypeRequest(
        ticketTypeModal.dataset.createUrl,
        "POST",
        { name },
      );

      if (!response.ok) {
        showCreateError(
          await errorMessageFrom(response, "Unable to create ticket type."),
        );
        return;
      }

      const { ticket_type: ticketType } = await response.json();
      addCustomTicketTypeOption(ticketType);
      applyTicketTypeSelection(ticketType.external_reference, ticketType.name);
      resetCreateForm();
      document.getElementById("ticket-type-modal-close")?.click();
    } catch (error) {
      console.error("Failed to create custom ticket type", error);
      showCreateError("Unable to create ticket type.");
    } finally {
      Rails.enableElement(createSubmitBtn);
    }
  };

  const setRemoveConfirmingState = (item, confirming) => {
    item.classList.toggle("is-confirming", confirming);
    item
      .querySelector(".ticket-type-option-name")
      ?.classList.toggle("tw-text-red-500", confirming);
    item
      .querySelector(".ticket-type-remove")
      ?.classList.toggle("tw-text-red-500", confirming);
  };

  const closeRemoveConfirmation = (item) => {
    setRemoveConfirmingState(item, false);
    item
      .querySelector(".ticket-type-remove-confirm")
      ?.classList.add("tw-hidden");
  };

  const closeAllRemoveConfirmations = () => {
    ticketTypeModal
      ?.querySelectorAll(".ticket-type-option-item.is-confirming")
      .forEach(closeRemoveConfirmation);
  };

  const openRemoveConfirmation = (item) => {
    closeAllRemoveConfirmations();
    setRemoveConfirmingState(item, true);
    item
      .querySelector(".ticket-type-remove-confirm")
      ?.classList.remove("tw-hidden");
  };

  const removeCustomTicketType = async (removeBtn) => {
    const externalReference = removeBtn.dataset.externalReference;
    clearModalError();
    removeBtn.disabled = true;

    try {
      const response = await ticketTypeRequest(
        ticketTypeModal.dataset.archiveUrlTemplate.replace(
          "__REF__",
          encodeURIComponent(externalReference),
        ),
        "DELETE",
      );

      if (!response.ok) {
        removeBtn.disabled = false;
        showModalError(
          await errorMessageFrom(response, "Unable to remove ticket type."),
        );
        return;
      }

      removeBtn.closest(".ticket-type-option-item")?.remove();
      clearRowsUsingTicketType(externalReference);
      if (customTicketTypeList && !customTicketTypeList.children.length) {
        customTicketTypeSection?.classList.add("tw-hidden");
      }
    } catch (error) {
      console.error("Failed to remove custom ticket type", error);
      removeBtn.disabled = false;
      showModalError("Unable to remove ticket type.");
    }
  };

  if (ticketTypeModal) {
    container.addEventListener("click", (e) => {
      const trigger = e.target.closest(".ticket-type-trigger");
      if (!trigger) return;
      trackActiveTicketTypeRow(trigger);
      clearModalError();
      resetCreateForm();
      closeAllRemoveConfirmations();
    });

    ticketTypeModal.addEventListener("click", (e) => {
      const removeBtn = e.target.closest(".ticket-type-remove");
      if (removeBtn) {
        openRemoveConfirmation(removeBtn.closest(".ticket-type-option-item"));
        return;
      }

      const keepBtn = e.target.closest(".ticket-type-remove-keep");
      if (keepBtn) {
        closeRemoveConfirmation(keepBtn.closest(".ticket-type-option-item"));
        return;
      }

      const confirmRemoveBtn = e.target.closest(".ticket-type-remove-accept");
      if (confirmRemoveBtn) {
        removeCustomTicketType(confirmRemoveBtn);
        return;
      }

      const option = e.target.closest(".ticket-type-option");
      if (option) {
        applyTicketTypeSelection(
          option.dataset.externalReference,
          option.dataset.name,
        );
        return;
      }

      if (e.target.closest("#create-custom-ticket-type-toggle")) {
        toggleCreateForm(createForm.classList.contains("tw-hidden"));
        return;
      }

      if (e.target.closest("#custom-ticket-type-submit")) {
        createCustomTicketType();
      }
    });

    createNameInput?.addEventListener("input", clearCreateError);
    createNameInput?.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      createCustomTicketType();
    });
  }

  const initCurrencyInputMasks = () => {
    container.querySelectorAll(".price-rands-input").forEach(bindRandsMask);
  };

  initCurrencyInputMasks();

  const addTicketOptionBtn = document.getElementById("add-ticket-option");
  if (addTicketOptionBtn) {
    addTicketOptionBtn.addEventListener("click", () => {
      const template = document.getElementById(
        "ticket_option_template",
      ).innerHTML;
      const newRow = template
        .replace(/__INDEX__|_INDEX_/g, ticketOptionIndex)
        .replace(/__NUMBER__|_NUMBER_/g, ticketOptionIndex + 1);
      container.insertAdjacentHTML("beforeend", newRow);
      container
        .querySelectorAll(".ticket-option-row:last-child .price-rands-input")
        .forEach(bindRandsMask);
      ticketOptionIndex++;
    });
  }

  container.addEventListener("click", (e) => {
    const removeBtn = e.target.closest(".remove-ticket-option");
    if (!removeBtn) return;

    const row = removeBtn.closest(".ticket-option-row");
    row.style.transition = "opacity 150ms ease-out";
    row.style.opacity = "0";

    setTimeout(() => {
      if (row !== null && row !== undefined) {
        row.remove();

        if (container.querySelectorAll(".ticket-option-row").length === 1) {
          ticketOptionIndex = 1;
        }
      }
    }, 150);
  });

  container.addEventListener("change", (e) => {
    const toggle = e.target.closest(".ticket-option-visible-toggle");
    if (!toggle) return;

    const row = toggle.closest(".ticket-option-row");
    const visible = e.target.checked;
    row.classList.toggle("ticket-option-row--hidden", !visible);

    const label = toggle.querySelector(".toggle-switch__label");
    if (label) label.textContent = visible ? "Visible" : "Hidden";

    // Mirrors _ticket_option_row.html.erb: hidden rows are read-only; saved rows keep
    // their ticket type and price, and their capacity once a live event has sales.
    const typeAndPriceLocked = !visible || row.dataset.locked === "true";
    const capacityLocked = !visible || row.dataset.capacityLocked === "true";
    const trigger = row.querySelector(".ticket-type-trigger");
    if (trigger) trigger.disabled = typeAndPriceLocked;
    const priceInput = row.querySelector(".price-rands-input");
    if (priceInput) priceInput.readOnly = typeAndPriceLocked;
    const capacityInput = row.querySelector(".ticket-option-capacity-input");
    if (capacityInput) capacityInput.readOnly = capacityLocked;
  });

  const clearTicketLimitFieldValue = () => {
    const input = document.getElementById("event_available_tickets");
    if (input) input.value = "";
  };

  const toggleTicketLimitFields = () => {
    const limitContainer = document.getElementById("ticket-limit-fields");
    if (!limitContainer) return;

    const show = isLimitedTicketCapacitySelected();
    limitContainer.classList.toggle("tw-hidden", !show);
    if (!show) clearTicketLimitFieldValue();
  };

  const clearTicketSalesCloseFieldValues = () => {
    const dateInput = document.getElementById("event_ticket_sales_close_date");
    const timeInput = document.getElementById("event_ticket_sales_close_time");
    if (dateInput && dateInput._flatpickr) dateInput._flatpickr.clear();
    else if (dateInput) dateInput.value = "";
    if (timeInput && timeInput._flatpickr) timeInput._flatpickr.clear();
    else if (timeInput) timeInput.value = "";
  };

  const clearTicketSalesCloseErrors = () => {
    document
      .querySelectorAll('[data-error-group="ticket-sales-close"]')
      .forEach((field) => {
        field.classList.remove("is-invalid");
      });
    const container = document.getElementById("ticket-sales-close-errors");
    if (container) {
      container.textContent = "";
      container.classList.add("tw-hidden");
      container.setAttribute("aria-hidden", "true");
    }
  };

  const toggleTicketSalesCloseFields = () => {
    const ticketSalesCloseContainer = document.getElementById(
      "ticket-sales-close-fields",
    );
    if (!ticketSalesCloseContainer) return;

    const show = isCloseBeforeEndSelected();
    ticketSalesCloseContainer.classList.toggle("tw-hidden", !show);

    if (!show) {
      clearTicketSalesCloseErrors();
      clearTicketSalesCloseFieldValues();
    }

    syncSalesCloseDateBounds();
  };

  initAddressAutocomplete({
    addressField: document.getElementById("event_address"),
    cityField: document.getElementById("event_city"),
    countryField: document.getElementById("event_country"),
    postalField: document.getElementById("event_postal_code"),
    latitudeField: document.getElementById("event_latitude"),
    longitudeField: document.getElementById("event_longitude"),
  });

  const catSearch = document.getElementById("category-search");
  const catDropdown = document.getElementById("category-dropdown");
  const catInputWrap = document.getElementById("category-input-wrap");
  const catInputGroup = document.getElementById("category-input-group");

  const syncCategoryInputState = () => {
    if (!catSearch) return;
    const hasSelected = _categoryState.selected.length > 0;
    const hasQuery = catSearch.value.trim().length > 0;
    const isFocused = document.activeElement === catSearch;
    const shouldFloat = hasSelected || hasQuery || isFocused;
    catSearch.placeholder = shouldFloat ? "" : "Categories *";
    if (catInputGroup) {
      catInputGroup.classList.toggle("is-floating", shouldFloat);
    }
  };

  const showCategoryLimitError = () => {
    const catWrap = document.getElementById("category-input-wrap");
    if (!catWrap) return;

    if (document.getElementById("category-limit-error")) return;

    catWrap.classList.add("tw-border-red-500");

    const errorEl = document.createElement("p");
    errorEl.id = "category-limit-error";
    errorEl.className =
      "form-field-error category-limit-error tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0";
    errorEl.textContent = `You can select up to ${MAX_EVENT_CATEGORIES} categories`;
    const parent =
      catWrap.closest(".form-group") ||
      catWrap.closest(".input-float-label") ||
      catWrap.parentNode;
    if (parent) parent.appendChild(errorEl);
  };

  const clearCategoryLimitError = () => {
    const catWrap = document.getElementById("category-input-wrap");
    if (!catWrap) return;

    catWrap.classList.remove("tw-border-red-500");
    const existing = document.getElementById("category-limit-error");
    if (existing) existing.remove();
  };

  const clearCategoryRequiredError = () => {
    const catWrap = document.getElementById("category-input-wrap");
    if (!catWrap || _categoryState.selected.length === 0) return;

    catWrap.classList.remove("tw-border-red-500");
    const parent =
      catWrap.closest(".form-group") ||
      catWrap.closest(".input-float-label") ||
      catWrap.parentNode;
    if (!parent) return;
    const existing = parent.querySelector(
      ".form-field-error:not(.category-limit-error)",
    );
    if (existing) existing.remove();
  };

  if (catSearch && catDropdown) {
    catInputWrap.addEventListener("click", () => {
      catSearch.focus();
    });

    catSearch.addEventListener("focus", () => {
      catDropdown.classList.remove("tw-hidden");
      syncCategoryInputState();
    });

    catSearch.addEventListener("blur", () => {
      syncCategoryInputState();
    });

    catSearch.addEventListener("input", () => {
      const query = catSearch.value.toLowerCase();
      const options = catDropdown.querySelectorAll(".category-option");
      let anyVisible = false;
      options.forEach((option) => {
        const name = option.getAttribute("data-name").toLowerCase();
        const match = name.indexOf(query) !== -1;
        option.classList.toggle("tw-hidden", !match);
        if (match) anyVisible = true;
      });
      const noResults = document.getElementById("category-no-results");
      if (noResults) noResults.classList.toggle("tw-hidden", anyVisible);
      syncCategoryInputState();
    });

    catDropdown.addEventListener("click", (e) => {
      const option = e.target.closest(".category-option");
      if (!option) return;

      const value = option.getAttribute("data-value");
      const name = option.getAttribute("data-name");
      const idx = _categoryFindIndex(value);

      if (idx === -1) {
        if (_categoryState.selected.length >= MAX_EVENT_CATEGORIES) {
          showCategoryLimitError();
          return;
        }

        _categoryState.selected.push({ value, name });
        option
          .querySelector(".category-option-check")
          .classList.remove("tw-opacity-0");
        option.classList.add("tw-bg-blue-50");
        clearCategoryLimitError();
        clearCategoryRequiredError();
      } else {
        _categoryState.selected.splice(idx, 1);
        option
          .querySelector(".category-option-check")
          .classList.add("tw-opacity-0");
        option.classList.remove("tw-bg-blue-50");
        clearCategoryLimitError();
      }

      _categoryRenderChips();
      _categorySyncHidden();
      catSearch.value = "";
      catSearch.dispatchEvent(new Event("input"));
      catSearch.focus();
    });

    document.addEventListener("click", (e) => {
      if (!catInputWrap.contains(e.target) && !catDropdown.contains(e.target)) {
        catDropdown.classList.add("tw-hidden");
        syncCategoryInputState();
      }
    });

    document.getElementById("category-chips").addEventListener("click", (e) => {
      const removeBtn = e.target.closest(".category-chip-remove");
      if (!removeBtn) return;

      const value = removeBtn.getAttribute("data-value");
      const idx = _categoryFindIndex(value);
      if (idx !== -1) _categoryState.selected.splice(idx, 1);
      clearCategoryLimitError();

      const options = catDropdown.querySelectorAll(".category-option");
      options.forEach((opt) => {
        if (opt.getAttribute("data-value") === value) {
          opt
            .querySelector(".category-option-check")
            .classList.add("tw-opacity-0");
          opt.classList.remove("tw-bg-blue-50");
        }
      });

      _categoryRenderChips();
      _categorySyncHidden();
    });

    syncCategoryInputState();
  }

  const _categoryFindIndex = (value) => {
    return _categoryState.selected.findIndex((item) => item.value === value);
  };

  const _categoryRenderChips = () => {
    const chipsContainer = document.getElementById("category-chips");
    if (!chipsContainer) return;

    const html = _categoryState.selected
      .map(
        (s) =>
          `<span class="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-blue-100 tw-text-primary tw-text-sm tw-font-semibold">` +
          `<span class="category-chip-label tw-max-w-[120px] sm:tw-max-w-[160px] tw-truncate" title="${escapeHtml(s.name)}">${escapeHtml(s.name)}</span>` +
          `<button type="button" class="category-chip-remove tw-text-primary tw-opacity-60 hover:tw-opacity-100 tw-cursor-pointer tw-leading-none tw-text-sm" data-value="${escapeHtml(s.value)}">&times;</button>` +
          `</span>`,
      )
      .join("");
    chipsContainer.innerHTML = html;
    syncCategoryInputState();
  };

  const _categorySyncHidden = () => {
    const hiddenContainer = document.getElementById("category-hidden-inputs");
    if (!hiddenContainer) return;

    const html = _categoryState.selected
      .map(
        (s) =>
          `<input type="hidden" name="event_form[category][]" value="${escapeHtml(s.value)}">`,
      )
      .join("");
    hiddenContainer.innerHTML = html;
  };

  const catSelector = document.getElementById("category-selector");
  const preselected = JSON.parse(
    (catSelector &&
      catSelector.dataset &&
      catSelector.dataset.selectedCategories) ||
      "[]",
  );
  preselected.forEach((ref) => {
    const option =
      catDropdown &&
      catDropdown.querySelector('.category-option[data-value="' + ref + '"]');
    if (option) {
      const name = option.getAttribute("data-name");
      _categoryState.selected.push({ value: ref, name });
      option
        .querySelector(".category-option-check")
        .classList.remove("tw-opacity-0");
      option.classList.add("tw-bg-blue-50");
    }
  });
  if (preselected.length > 0) {
    _categoryRenderChips();
    _categorySyncHidden();
  }
  syncCategoryInputState();

  document
    .querySelectorAll('input[name="event_form[ticket_sales_duration]"]')
    .forEach((radio) => {
      radio.addEventListener("change", toggleTicketSalesCloseFields);
    });
  document
    .querySelectorAll('input[name="event_form[ticket_capacity]"]')
    .forEach((radio) => {
      radio.addEventListener("change", toggleTicketLimitFields);
    });
  toggleTicketSalesCloseFields();
  toggleTicketLimitFields();

  document.addEventListener("click", (e) => {
    if (e.target.closest(".category-chip-remove")) {
      clearCategoryLimitError();
    }
  });
});
